import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Grid, Paper, Typography, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, useTheme, useMediaQuery, Button, IconButton
} from '@mui/material';
import { School as SchoolIcon } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import BookIcon from '@mui/icons-material/Book';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import axios from 'axios';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from 'recharts';
import EventsWidget from '../components/Dashboard/EventsWidget';
import { getApiUrl } from '../config/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const CircularProgressWidget = ({ title, count, subtitle, icon, percentage, isSmallMobile }: any) => (
    <Paper elevation={0} sx={{ p: isSmallMobile ? 2 : 3, bgcolor: '#ffffff', borderRadius: 4, position: 'relative', height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    {React.cloneElement(icon, { sx: { fontSize: 24, color: '#1a237e' } })}
                </Box>
                <Typography variant={isSmallMobile ? "h5" : "h4"} fontWeight={700} color="#1a237e">{count}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.65rem' }}>{subtitle}</Typography>
            </Box>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Box sx={{
                    width: 48, height: 48, borderRadius: '50%',
                    border: '4px solid #f0f2f5',
                    borderTopColor: '#1a237e',
                    transform: `rotate(${percentage * 3.6}deg)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', color: '#1a237e', transform: `rotate(-${percentage * 3.6}deg)` }}>{percentage}%</Typography>
                </Box>
            </Box>
        </Stack>
    </Paper>
);


const DashboardHome = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<any>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('reports/dashboard-stats'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(res.data);
        } catch (error) {
            console.error('Dashboard fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
            const parsed = JSON.parse(userInfo);
            const userObj = parsed.user || parsed;
            setUser(userObj);

            if (userObj.role === 'PARENT') {
                navigate('/dashboard/parent-portal');
            } else if (userObj.role === 'RECEPTIONIST') {
                navigate('/dashboard/receptionist');
            }
        }
        fetchDashboardData();
    }, [navigate, fetchDashboardData]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress thickness={5} size={60} sx={{ color: '#1a237e' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: isMobile ? 0 : 0 }}>
            <Grid container spacing={4}>
                {/* Left Content Area (8/12) */}
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Stack spacing={4}>
                        {/* Summary Stats */}
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6, md: (user?.role === 'SUPER_ADMIN' || user?.role === 'SCHOOL_ADMIN' || user?.role === 'BURSAR') ? 4 : 12 }}>
                                <CircularProgressWidget
                                    title="STUDENTS"
                                    count={stats?.demographics?.students?.total || 0}
                                    subtitle={`ACTIVE LEARNERS`}
                                    icon={<SchoolIcon />}
                                    isSmallMobile={isSmallMobile}
                                    percentage={stats?.demographics?.students?.total > 0
                                        ? Math.round((stats.demographics.students.male / stats.demographics.students.total) * 100)
                                        : 0}
                                />
                            </Grid>
                            {(user?.role === 'SUPER_ADMIN' || user?.role === 'SCHOOL_ADMIN' || user?.role === 'BURSAR' || user?.role === 'ACCOUNTANT' || user?.role === 'PRINCIPAL') && (
                                <>
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <CircularProgressWidget
                                            title="TOTAL REVENUE"
                                            count={`$${(stats?.kpis?.revenue || 0).toLocaleString()}`}
                                            subtitle="TERM COLLECTIONS"
                                            icon={<BookIcon />}
                                            isSmallMobile={isSmallMobile}
                                            percentage={Math.round((stats?.kpis?.revenue / (stats?.kpis?.revenue + stats?.kpis?.outstanding)) * 100) || 0}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <CircularProgressWidget
                                            title="OUTSTANDING"
                                            count={`$${(stats?.kpis?.outstanding || 0).toLocaleString()}`}
                                            subtitle="RECOVERY TARGET"
                                            icon={<PersonIcon />}
                                            isSmallMobile={isSmallMobile}
                                            percentage={Math.round((stats?.kpis?.outstanding / (stats?.kpis?.revenue + stats?.kpis?.outstanding)) * 100) || 0}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>

                        {/* Charts Area - Only for Finance Roles */}
                        {(user?.role === 'SUPER_ADMIN' || user?.role === 'SCHOOL_ADMIN' || user?.role === 'BURSAR' || user?.role === 'ACCOUNTANT' || user?.role === 'PRINCIPAL') && (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper elevation={0} sx={{ p: isSmallMobile ? 3 : 4, borderRadius: '32px', height: '100%', border: '1px solid #edf2f7' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                                        <Box>
                                            <Typography variant="h6" fontWeight={700} color="primary">Collection Velocity</Typography>
                                            <Typography variant="caption" color="text.secondary">Real-time treasury influx</Typography>
                                        </Box>
                                    </Stack>
                                    <Box sx={{ height: 280 }}>
                                        {stats?.trends && stats.trends.length > 0 && (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={stats.trends}>
                                                    <defs>
                                                        <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#1a237e" stopOpacity={0.1} />
                                                            <stop offset="95%" stopColor="#1a237e" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#edf2f7" />
                                                    <XAxis dataKey="month" fontSize={11} axisLine={false} tickLine={false} tick={{ fill: '#A3AED0' }} />
                                                    <YAxis fontSize={11} axisLine={false} tickLine={false} tick={{ fill: '#A3AED0' }} tickFormatter={(val) => `$${val}`} />
                                                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #edf2f7' }} />
                                                    <Line type="monotone" dataKey="total" stroke="#1a237e" strokeWidth={4} dot={{ r: 4, fill: '#1a237e', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 8 }} />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper elevation={0} sx={{ p: isSmallMobile ? 3 : 4, borderRadius: '32px', height: '100%', border: '1px solid #edf2f7' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                                        <Box>
                                            <Typography variant="h6" fontWeight={700} color="primary">Asset Distribution</Typography>
                                            <Typography variant="caption" color="text.secondary">Inflow by channel</Typography>
                                        </Box>
                                    </Stack>
                                    <Box sx={{ height: 280 }}>
                                        {stats?.methods && stats.methods.length > 0 && (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={stats.methods}
                                                        innerRadius={70}
                                                        outerRadius={95}
                                                        paddingAngle={8}
                                                        dataKey="value"
                                                        stroke="none"
                                                    >
                                                        {stats.methods.map((entry: any, index: number) => (
                                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #edf2f7' }} />
                                                    <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '0.8rem' }} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                        )}

                        {/* Recent Transactions - Only for Finance Roles */}
                        {(user?.role === 'SUPER_ADMIN' || user?.role === 'SCHOOL_ADMIN' || user?.role === 'BURSAR' || user?.role === 'ACCOUNTANT' || user?.role === 'PRINCIPAL') ? (
                            <Paper elevation={0} sx={{ p: isSmallMobile ? 3 : 4, borderRadius: 3, border: '1px solid #edf2f7' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                                    <Typography variant="h6" fontWeight={700} color="primary">Recent Activity</Typography>
                                    <Button
                                        size="small"
                                        sx={{ fontWeight: 700, borderRadius: '8px' }}
                                        onClick={() => navigate('/dashboard/ledger')}
                                    >
                                        View All
                                    </Button>
                                </Stack>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: '#A3AED0', fontWeight: 700, border: 'none', fontSize: '0.75rem' }}>TRANSACTION</TableCell>
                                                <TableCell sx={{ color: '#A3AED0', fontWeight: 700, border: 'none', fontSize: '0.75rem' }}>STUDENT</TableCell>
                                                <TableCell sx={{ color: '#A3AED0', fontWeight: 700, border: 'none', fontSize: '0.75rem' }}>DATE</TableCell>
                                                <TableCell sx={{ color: '#A3AED0', fontWeight: 700, border: 'none', fontSize: '0.75rem', textAlign: 'right' }}>VALUATION</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {stats?.recentActivities?.map((row: any, index: number) => (
                                                <TableRow key={index} sx={{ '&:last-child td': { border: 'none' } }}>
                                                    <TableCell sx={{ borderBottom: '1px solid #F4F7FE', py: 2.5 }}>
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <Box sx={{
                                                                width: 32, height: 32, borderRadius: '8px',
                                                                bgcolor: row.type === 'INVOICE' ? '#FFF5F5' : (row.type === 'RECEIPT' ? '#E6FFFA' : '#E3F2FD'),
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                            }}>
                                                                <Box sx={{
                                                                    width: 6, height: 6, borderRadius: '50%',
                                                                    bgcolor: row.type === 'INVOICE' ? '#E53E3E' : (row.type === 'RECEIPT' ? '#38A169' : '#1E88E5')
                                                                }} />
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={800} color="#1B2559">
                                                                    {row.type === 'POS_SALE' ? 'POS SALE' : row.type}
                                                                </Typography>
                                                                <Typography variant="caption" color="#A3AED0" sx={{ fontSize: '0.7rem' }}>{row.ref}</Typography>
                                                            </Box>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell sx={{ borderBottom: '1px solid #F4F7FE', fontWeight: 700, color: '#1B2559' }}>{row.student}</TableCell>
                                                    <TableCell sx={{ borderBottom: '1px solid #F4F7FE', color: '#A3AED0', fontWeight: 600 }}>{new Date(row.date).toLocaleDateString()}</TableCell>
                                                    <TableCell sx={{ borderBottom: '1px solid #F4F7FE', fontWeight: 900, textAlign: 'right', color: '#1B2559' }}>
                                                        ${row.amount.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        ) : (
                            <Paper elevation={0} sx={{ p: isSmallMobile ? 3 : 4, borderRadius: 3, border: '1px solid #edf2f7', bgcolor: '#f8fafc' }}>
                                <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>Welcome Back, {user?.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Use the sidebar to manage your classes, student attendance, and assignments.
                                </Typography>
                            </Paper>
                        )}
                    </Stack>
                </Grid>

                {/* Right Content Area (4/12) */}
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Stack spacing={4}>
                        {/* Elite Calendar */}
                        <Paper elevation={0} sx={{ p: isSmallMobile ? 3 : 4, borderRadius: 4, bgcolor: '#ffffff', border: '1px solid #edf2f7' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                                <Typography variant="h6" fontWeight={700} color="#1a237e">Calendar</Typography>
                                <IconButton size="small">
                                    <MoreHorizIcon />
                                </IconButton>
                            </Stack>
                            <Grid container spacing={1} sx={{ textAlign: 'center' }}>
                                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                                    <Grid size={1.7} key={day}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary">{day}</Typography>
                                    </Grid>
                                ))}
                                {Array.from({ length: 30 }).map((_, i) => (
                                    <Grid size={1.7} key={i}>
                                        <Box sx={{
                                            p: 0.8,
                                            borderRadius: 2,
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            bgcolor: i === 15 ? '#ffab00' : 'transparent',
                                            color: i === 15 ? '#000' : (i % 7 === 0 ? 'text.secondary' : '#1a237e'),
                                            transition: 'all 0.2s',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: '#f8f9fa' }
                                        }}>
                                            {i + 1}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>

                        {/* Events Section */}
                        <EventsWidget />
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DashboardHome;
